/* eslint-disable react/react-in-jsx-scope */
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { FileText, FolderOpen, FolderTree, Hash, MapPin } from "lucide-react";
import type { ReactNode } from "react";

type FolderDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: {
    label: string;
    fullPath: string;
    children?: unknown[];
    deleteFolderTarget?: {
      kind?: "custom" | "default";
      defaultKey?: string;
    };
  } | null;
  fileCount: number;
  folderCount: number;
};

const DetailCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value?: string | number | null;
}) => (
  <div className="flex min-w-0 gap-3 rounded-md border border-[#dbe6ef] bg-white px-4 py-3 shadow-sm">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#eef4e8] text-[#1d5215]">
      {icon}
    </div>
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5f7180]">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm font-medium text-[#1f2937]">
        {value || "Non renseigné"}
      </dd>
    </div>
  </div>
);

const getFolderTypeLabel = (folder: FolderDetailsDialogProps["folder"]) => {
  if (folder?.deleteFolderTarget?.kind === "custom") {
    return "Dossier personnalisé";
  }

  if (folder?.deleteFolderTarget?.kind === "default") {
    return "Dossier par défaut";
  }

  return "Dossier";
};

export const FolderDetailsContent = ({
  folder,
  fileCount,
  folderCount,
}: Pick<FolderDetailsDialogProps, "folder" | "fileCount" | "folderCount">) => (
  <>
    <header className="border-b border-[#e5e7eb] bg-[#f8fafc] px-6 py-5 text-left">
      <div className="flex min-w-0 items-start gap-4 pr-8">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-[#1d5215] text-white shadow-sm">
          <FolderOpen className="h-7 w-7" />
        </div>
        <div className="min-w-0">
          <h2 className="break-words text-xl font-semibold text-[#1f2937]">
            {folder?.label || "Dossier"}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#5f7180]">
            <span className="rounded-full bg-white px-3 py-1 font-medium text-[#1d5215]">
              {getFolderTypeLabel(folder)}
            </span>
            <span>
              {fileCount} fichier{fileCount > 1 ? "s" : ""} · {folderCount} sous-dossier
              {folderCount > 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>
    </header>

    <div className="space-y-5 px-6 py-5">
      <dl className="grid gap-3">
        <DetailCard
          icon={<FileText className="h-4 w-4" />}
          label="Fichiers"
          value={fileCount}
        />
        <DetailCard
          icon={<FolderTree className="h-4 w-4" />}
          label="Sous-dossiers"
          value={folderCount}
        />
        <DetailCard
          icon={<Hash className="h-4 w-4" />}
          label="Type"
          value={getFolderTypeLabel(folder)}
        />
        <DetailCard
          icon={<MapPin className="h-4 w-4" />}
          label="Emplacement"
          value={folder?.fullPath}
        />
      </dl>
    </div>
  </>
);

const FolderDetailsDialog = ({
  open,
  onOpenChange,
  folder,
  fileCount,
  folderCount,
}: FolderDetailsDialogProps) => (
  <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
    <SheetContent
      side="right"
      hideOverlay
      onInteractOutside={(event) => event.preventDefault()}
      className="top-[7rem] bottom-4 h-auto w-full overflow-auto rounded-l-md p-0 sm:top-[8.5rem] sm:max-w-[520px]"
    >
      <FolderDetailsContent folder={folder} fileCount={fileCount} folderCount={folderCount} />
    </SheetContent>
  </Sheet>
);

export default FolderDetailsDialog;
